import React, {useEffect} from "react";
import {Button} from "@mui/material";
import TableRow from "@mui/material/TableRow";
import CatalogFilters, {addFiltersToTable} from "../../../../../components/CatalogFilters/catalog_filters";
import {
    ButtonGroup,
    ClickAwayListener,
    Fade,
    Grid,
    Paper,
    Popper,
    TableCell,
    Typography
} from "@mui/material";
import CatalogManagerTable from "../../../../../components/CatalogManagerTable";
import { SavedSearchManager } from "../../../../../components/saved_search";
import { getTableConfig } from '../../../../../components/saved_search/utils/getConfig';
import { applySavedSearchConfig } from '../../../../../components/saved_search/utils/applyConfig';
import {messageStatusOptions, updatePostStatus} from "../../../../../utils/utils";
import Config from "../../../../../config";
import axios from "axios";
import CatalogCheckbox from "../../../../../components/EnhancedCheckbox";
import EnhancedSelect from "../../../../../components/EnhancedSelect";
import {BulkUpdateStatus} from "./catalog_results";
import EnhancedErrorDialog from "../../../../../components/EnhancedErrorDialog";
import UpdatingDialog from "../../../../../components/Dialogs/UpdatingDialog";
import { tableRowSx, tableCellTruncateSx } from "../../../../../theme";

// region TypeScript Interfaces
interface MessageFilterButtonsProps {
    table: any;
    metaData: Record<string, number>;
    currentCatalog?: string;
    isRetailer?: boolean;
}

interface FilterButtonInnerProps {
    option: string;
    count: number;
    field: string;
    customFilter?: any;
    selectedValue: string;
    previousFilter: any;
}

interface MessageFiltersProps {
    table: any;
    currentCatalog: string;
    isRetailer: boolean;
}

interface MessageResultsProps {
    table: any;
    config: any;
    setConfig: (fn: (prevState: any) => any) => void;
    history: any;
    location: any;
    currentCatalog: string;
    isRetailer: boolean;
    condensed?: boolean;
    filterShowExceptions?: any;
}
// endregion


function MessageFilterButtons(props: MessageFilterButtonsProps) {
    const FilterButton = ({option, count, field, customFilter, selectedValue, previousFilter}: FilterButtonInnerProps) => {
        let filter = customFilter !== undefined
            ? customFilter
            : {
                field: field,
                operator: "eq",
                value: option,
            }

        const isSelected = selectedValue === option;

        return (
            <Button variant="outlined"
                color={isSelected ? "primary" : "inherit"}
                onClick={async () => {
                    if (option === "All" && previousFilter != null)
                        props.table.extensions.onRemoveFilter(previousFilter);
                    else if (option === "All") {
                        // Do nothing
                    } else {
                        if (previousFilter != null) {
                            props.table.extensions.updateFilter(filter);
                        } else
                            props.table.extensions.onAddFilter(filter);
                    }
                }}
            >
                {option} ({count || 0})
            </Button>
        )
    }

    let statusButtons: React.ReactNode = <></>

    let selectedValue = "All";
    let previousFilter: any = null;

    if (props.table.state.filter != null) {
        let foundStatus = props.table.state.filter.filters.find((f: any) => f.field === "Status");
        if (foundStatus !== undefined) {
            selectedValue = foundStatus.value;
            previousFilter = props.table.state.filter.filters.find((f: any) => f.field === "Status");
        }
    }

    statusButtons = messageStatusOptions.map((option: string, index: number) => {
        return (
            <FilterButton key={index} option={option} field={"Status"} count={props.metaData[option]}
                          selectedValue={selectedValue} previousFilter={previousFilter}
            />
        )
    });

    return (
        <>
            {statusButtons}
        </>
    );
}

function MessageFilters(props: MessageFiltersProps) {
    const {isRetailer} = props;
    const [metaData, setMetaData] = React.useState<Record<string, number>>({});

    useEffect(() => {
        const getMetaData = async () => {
            const url = Config.api + "/odata/Company/VwCompanyPosts";
            const filter = `(Category eq 'POC')`;

            try {
                let response = await axios.get(url, {
                    params: {
                        $count: true,
                        $filter: filter,
                    }
                });

                // Grab status counts
                let statusCounts: Record<string, number> = {};
                for (let status of messageStatusOptions) {
                    statusCounts[status] = response.data.value.filter((item: any) => item.Status === status).length;

                }
                statusCounts["All"] = response.data["@odata.count"];
                setMetaData(statusCounts);
            } catch (e) {

            }
        }

        getMetaData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.currentCatalog]);

    return (
        <Grid item container style={{gap: "8px", flexWrap: "wrap", flex: 1, minWidth: 0}}>
            <MessageFilterButtons
                currentCatalog={props.currentCatalog} isRetailer={isRetailer}
                table={props.table}
                metaData={metaData}
            />
        </Grid>
    )
}


export default function MessageResults(props: MessageResultsProps) {
    let table = props.table;

    const isSelectable = true;

    const [error, setError] = React.useState<any>(null);
    const [updating, setUpdating] = React.useState(false);

    const [updateStatusesOpen, setUpdateStatusesOpen] = React.useState(false);
    const [updateStatusesAnchorEl, setUpdateStatusesAnchorEl] = React.useState<HTMLElement | null>(null);
    const [updateStatusValue, setUpdateStatusValue] = React.useState<any>(null);

    //region Functions
    const bulkUpdateStatus = async (status: string) => {
        setUpdating(true);
        setError(null);
        const selectedMessages = table.data.filter((row: any) => props.table.state.selected.includes(row.Id));

        // Run all updates, then aggregate failures into a single error (instead of
        // last-write-wins setError calls, which surfaced one arbitrary failure).
        const responses = await Promise.all(
            selectedMessages.map((message: any) => updatePostStatus(message.Id, status))
        );
        const failures = responses.filter((response: any) => response !== true);

        if (failures.length > 0) {
            const detail = Array.from(
                new Set(failures.map((f: any) => f.errorMessage).filter(Boolean))
            ).join("\n");
            setError({
                open: true,
                errorHeader: failures[0].errorHeader || "Error updating post status!",
                errorMessage: failures.length < selectedMessages.length
                    ? (detail
                        ? `${failures.length} of ${selectedMessages.length} status updates failed:\n${detail}`
                        : `${failures.length} of ${selectedMessages.length} status updates failed.`)
                    : (detail || `All ${selectedMessages.length} status updates failed.`),
            });
        }

        await new Promise(r => setTimeout(r, 900));
        props.setConfig((prevState) => ({...prevState, refresh: !props.config.refresh}));
        setUpdating(false);
    }
    //endregion

    // Format CatalogManagerTable Data Into Rows //
    const generateRows = () => {
        return table.data.map((row: any, rowIndex: number) => {
            return (
                <TableRow key={rowIndex} sx={tableRowSx} style={{height: "70px", cursor: "pointer"}}>
                    {isSelectable && (
                        <TableCell padding="checkbox" style={{width: '3em'}}>
                            <CatalogCheckbox checked={props.table.extensions.isSelected(row.Id)}
                                             onChange={(e) => props.table.extensions.handleClick(e, row.Id)}
                                             onClick={(e) => e.stopPropagation()}
                            />
                        </TableCell>
                    )}

                    {props.config.columns.map((column: any, colIndex: number) => {
                        if (props.table.extensions.isColumnHidden(column)) return null;
                        if (column.hidden && !column.toggleable) return null;

                        let content = column.template
                            ? column.template(row, column, colIndex, rowIndex,
                                (filter: any) => addFiltersToTable([filter], props.table),
                                props.isRetailer
                            )
                            : row[column.id];
                        return (
                            <TableCell
                                style={{
                                    padding: !isSelectable ? undefined : "0 6px 0 0",
                                }}
                                key={`${rowIndex}-${colIndex}`}>
                                {content}
                            </TableCell>
                        )
                    })
                    }

                    <TableCell sx={tableCellTruncateSx} style={{width: 0, padding: "4px 0"}}
                               onClick={(e) => {
                                   e.stopPropagation();
                                   e.preventDefault();
                               }}
                    >
                        <Grid container style={{flexDirection: "column", margin: "0"}}>
                        </Grid>
                    </TableCell>
                </TableRow>
            )
        })
    };

    return (
        <>
            {/*region Saved Search*/}
            {<Paper style={{width: "100%", padding: '10px 12px'}}>
                <SavedSearchManager
                    tableType="catalog-messages"
                    tableState={getTableConfig(props.table, props.filterShowExceptions, props.config)}
                    initialFilters={props.config.filter}
                    variant={'standard'}
                    onConfigApplied={(config) => {
                        applySavedSearchConfig(config, {
                            table: props.table,
                            columns: props.config.columns,
                            setConfig: props.setConfig
                        });
                    }}
                />
            </Paper>}
            {/*endregion*/}
            {/*region Catalog Filters*/}
            <Paper style={{width: "100%"}}>
                <Grid container>
                    <CatalogFilters
                        Filters={
                            <MessageFilters
                                currentCatalog={props.currentCatalog} isRetailer={props.isRetailer}
                                table={props.table}
                            />
                        }
                        table={props.table}
                        config={props.config} setConfig={props.setConfig}
                        history={props.history} location={props.location}
                        disableColumnSelector={true}
                        disableSearch={true}
                        filterShowExceptions={["Category"]}
                    />
                </Grid>
            </Paper>
            {/*endregion*/}
            {/*region Table */}
            <Paper style={{width: "100%"}}>
                <Grid container style={{overflow: "auto", flexDirection: "column"}}>
                    <CatalogManagerTable
                        table={table}
                        isSelectable={isSelectable}
                        config={props.config}
                        generateRows={generateRows}
                        bulkActions={(
                                <ButtonGroup variant="outlined"
                                             style={{
                                                 visibility: props.table.state.selected.length > 0 ? "visible" : "hidden",
                                                 opacity: props.table.state.selected.length > 0 ? 1 : 0,
                                             }}>
                                    <Button variant="outlined" onClick={(e) => {
                                        setUpdateStatusesOpen(true);
                                        setUpdateStatusesAnchorEl(e.currentTarget);
                                    }}>Update Status</Button>
                                </ButtonGroup>
                            )}
                    />
                </Grid>
            </Paper>
            {/*endregion*/}
            {/*region Dialogs*/}
            <UpdatingDialog open={updating}/>
            <EnhancedErrorDialog
                open={error?.open}
                errorHeader={error?.errorHeader}
                errorMessage={error?.errorMessage}
                onClose={() => setError(null)}
            />
            <Popper open={updateStatusesOpen} anchorEl={updateStatusesAnchorEl} transition
                    placement={"bottom-start"}>
                {({TransitionProps}) => (
                    <ClickAwayListener onClickAway={() => {
                        setUpdateStatusesOpen(false);
                        setUpdateStatusesAnchorEl(null)
                    }}>
                        <Fade {...TransitionProps} timeout={350}>
                            <BulkUpdateStatus container>
                                <Typography>Update Status for selected messages</Typography>

                                <EnhancedSelect
                                    value={updateStatusValue}
                                    onChange={setUpdateStatusValue}
                                    placeholder={"Status"}
                                    options={messageStatusOptions.filter((option) => option !== "All").map((option) => {
                                        return {label: option, value: option}
                                    })}
                                />

                                <Grid item container style={{width: "100%", gap: "8px"}} justifyContent={"flex-end"}>
                                    <Button variant="outlined"
                                            style={{padding: "6px 12px"}}
                                            onClick={() => {
                                                setUpdateStatusesOpen(false);
                                                setUpdateStatusesAnchorEl(null);
                                            }}
                                    >
                                        Cancel
                                    </Button>
                                    <Button variant="outlined" color="primary"
                                        style={{padding: "6px 12px"}}
                                        onClick={() => {
                                            if (updateStatusValue == null) return;
                                            bulkUpdateStatus(updateStatusValue.value);
                                            setUpdateStatusesOpen(false);
                                            setUpdateStatusesAnchorEl(null);
                                        }}
                                    >
                                        Save
                                    </Button>
                                </Grid>
                            </BulkUpdateStatus>
                        </Fade>
                    </ClickAwayListener>
                )}
            </Popper>
            {/*endregion*/}
        </>
    );
}
